import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import {
  WHMaintenanceNotificationDOM,
  WHFeatureKeyENUM,
  WHIconENUM,
  WHLoginDataService,
  WHPermissionCheckModule,
} from '@workheld/workheld-shared-lib';
import { CustomerStaticDataModule } from 'src/app/app-modules-pages/customer/customer-static-data/customer-static-data.module';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { WorkObjectStaticDataModule } from 'src/app/app-modules-pages/work-object/work-object-static-data/work-object-static-data.module';
import { MatButtonModule } from '@angular/material/button';
import { AppSharedModule } from 'src/app/app-shared/app-shared.module';
import { MaintenanceNotificationsStore } from '../entry-maintenance-notifications.store';
import { UIService } from 'src/app/app-services/ui.service';
import { WorkerStaticDataModule } from 'src/app/app-modules-pages/worker/worker-static-data/worker-static-data.module';
import {
  MetadataEnum,
  WHEnumDataService,
} from 'src/app/app-services-helper/w-h-enum-helper.service';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WHFileDragNDropComponent } from 'src/app/app-shared/components/files-dnd/w-h-files-dnd.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { StatusColorPipe } from 'src/app/app-shared/shared-pipes/status-color.pipe';
import { WHButtonComponent } from 'src/app/app-shared/components/buttons/w-h-button/w-h-button.component';
import { FormReferenceService } from 'src/app/app-services-helper/form-reference.service';
import { IWHUpdateMaintenanceNotificationPayload } from 'src/app/app-services-async/maintenance-notification-async.service';
import { map, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

/** MN text fields the user may edit after creation (WHP-12151). */
type EditableMaintenanceNotificationField =
  'title' | 'description' | 'managerComment' | 'contactPerson';

/** MN statuses that block any edit, mirroring the backend edit guard. */
const TERMINAL_MN_STATUSES = ['DONE', 'COMPLETED', 'REJECTED'];

const EDITABLE_MN_FIELDS: EditableMaintenanceNotificationField[] = [
  'title',
  'description',
  'managerComment',
  'contactPerson',
];

/** Form control name -> update-command field. */
const MN_PAYLOAD_KEY_BY_FIELD: Record<
  EditableMaintenanceNotificationField,
  keyof IWHUpdateMaintenanceNotificationPayload
> = {
  title: 'title',
  description: 'description',
  managerComment: 'managerComment',
  contactPerson: 'contactPerson',
};

@UntilDestroy()
@Component({
  selector: 'app-entry-maintenance-notification-details',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    ReactiveFormsModule,
    CustomerStaticDataModule,
    MatGridList,
    MatGridTile,
    WorkObjectStaticDataModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSelectModule,
    AppSharedModule,
    WorkerStaticDataModule,
    DatePipe,
    WHFileDragNDropComponent,
    StatusColorPipe,
    WHPermissionCheckModule,
    WHButtonComponent,
  ],
  template: `
    @let entryDOM = store.maintenanceNotification();
    @let bomNode = store.bomNode();
    @let rootBomNode = store.rootBomNode();
    @let loading = store.isLoading();
    @let hasChanges = hasUnsavedChanges();

    <!--
      Read value + pen icon, or the input once the field is in edit mode.
      Mirrors the standalone MN status change (HKM case).
    -->
    <ng-template
      #editableTextField
      let-label="label"
      let-field="field"
      let-control="control"
      let-value="value"
      let-rows="rows"
    >
      <div class="flex-1 min-w-0 editable-field">
        <div class="w-h-label entry-detail-label">{{ label | translate }}</div>
        @if (isFieldEditing(field)) {
          <div class="field-edit-wrapper">
            <mat-form-field class="field-edit-input">
              @if (rows) {
                <textarea
                  matInput
                  [rows]="rows"
                  [formControl]="control"
                  [attr.data-cy]="
                    'maintenance-notification-' + field + '-input'
                  "
                ></textarea>
              } @else {
                <input
                  matInput
                  autocomplete="off"
                  [formControl]="control"
                  [attr.data-cy]="
                    'maintenance-notification-' + field + '-input'
                  "
                />
              }
            </mat-form-field>
          </div>
        } @else {
          <div class="flex--r-c gap--8 min-w-0 field-value-wrapper">
            <div
              class="default-value field-value-clamp"
              [style.-webkit-line-clamp]="rows || 1"
              [matTooltip]="value"
              [matTooltipDisabled]="!value"
            >
              {{ value || '-' }}
            </div>
            @if (canEditTextFields()) {
              <button
                mat-icon-button
                *appIsAllowed="{
                  permission: 'flow.coordinator.maintenance_notif.write',
                }"
                class="field-edit-button"
                [attr.data-cy]="
                  'maintenance-notification-' + field + '-edit-button'
                "
                [matTooltip]="'app.edit.label' | translate"
                (click)="startFieldEdit(field)"
              >
                <mat-icon class="edit-icon">edit</mat-icon>
              </button>
            }
          </div>
        }
      </div>
    </ng-template>

    <div class="detail-card-header flex--r-c-sb p--12">
      <div class="flex--r-c gap--12">
        <mat-icon>warning</mat-icon>
        {{ 'app.ui.maintenancenotification.label' | translate }}
      </div>
      <button
        mat-icon-button
        data-cy="maintenance-notification-close-button"
        [matTooltip]="'app.close.label' | translate"
        [disabled]="loading"
        (click)="handleClose()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="card-body flex--col min-w-0">
      <mat-grid-list cols="4" class="m--16" rowHeight="65px" gutterSize="16px">
        <!-- Row 1: Customer | Work Object | SAP Number | MN Status -->
        <mat-grid-tile>
          <ng-bee-customer-static-data
            [withLabel]="true"
            [detailURL]="
              entryDOM?.customerCachedDetailURL ||
              entryDOM?.workObjectDetails?.customerDetailURL
            "
          >
          </ng-bee-customer-static-data>
        </mat-grid-tile>

        <mat-grid-tile data-cy="generated-project-detail">
          @if (isProjectsOrCasesEnabled()) {
            @if (!entryDOM?.workObjectDetails?.clientUrl) {
              <span>
                <ng-bee-work-object-static-data
                  [isSimpleData]="true"
                  [withTitleLabel]="true"
                  [detailURL]="
                    entryDOM?.workObjectCachedDetailURL ||
                    entryDOM?.generatedWorkObjectDetailURL
                  "
                >
                </ng-bee-work-object-static-data>
              </span>
            } @else {
              <div class="flex--col min-w-0">
                <div class="w-h-label html-overflow" style="margin-bottom: 0px">
                  {{ 'workobject.model.root' | translate }}
                </div>
                <div class="flex--c-c-l">
                  <button
                    style="width: 100%; max-width: max-content"
                    mat-button
                    class="generated-workobject-button"
                    color="primary"
                    disableRipple
                    matTooltip="{{ entryDOM?.generatedWorkObjectExtID }} {{
                      entryDOM?.workObjectDetails?.title
                    }}"
                    (click)="navigateToProjectDetails()"
                  >
                    <div class="html-overflow" style="max-width: unset">
                      {{ entryDOM?.generatedWorkObjectExtID }} -
                      {{ entryDOM?.workObjectDetails?.title }}
                    </div>
                    <mat-icon iconPositionEnd>open_in_new</mat-icon>
                  </button>
                </div>
              </div>
            }
          }
        </mat-grid-tile>

        <mat-grid-tile colspan="2">
          @if (isExternalReplyEnabled()) {
            <div class="wh-assignment-box flex--r-c flex-1 p--8">
              <span class="flex-1" style="max-width: 52%; min-width: 52%">
                <div class="w-h-label html-overflow">
                  {{ 'app.ui.sapReport.label' | translate }}
                </div>
                <div
                  class="html-overflow"
                  [matTooltip]="entryDOM?.extId"
                  [matTooltipDisabled]="!entryDOM?.extId"
                >
                  {{ entryDOM?.extId ?? '-' }}
                </div>
              </span>
              <span class="flex-1">
                <div class="w-h-label html-overflow">
                  {{
                    'app.ui.maintenanceNotification.status.label' | translate
                  }}
                </div>
                @if (isManualStatusChangeEnabled() && isStatusEditing()) {
                  <mat-form-field
                    class="w-100 status-select"
                    data-cy="maintenance-notification-status-select"
                  >
                    <mat-select
                      [formControl]="maintenanceNotificationStatusControl"
                    >
                      @for (
                        status of $ENUMS().MAINTENANCE_NOTIFICATION_STATUS;
                        track status.id
                      ) {
                        <mat-option [value]="status.id">{{
                          status.translateKey | translate
                        }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                } @else if (entryDOM?.maintenanceNotificationStatus) {
                  <div class="flex--r-c status-wrapper">
                    <div
                      class="wh-status"
                      [ngClass]="
                        entryDOM?.maintenanceNotificationStatus
                          | statusColor: 'maintenanceNotification'
                      "
                    >
                      {{
                        $ENUMS_OBJECT().MAINTENANCE_NOTIFICATION_STATUS[
                          entryDOM?.maintenanceNotificationStatus
                        ] | translate
                      }}
                    </div>
                    @if (isManualStatusChangeEnabled()) {
                      <button
                        mat-icon-button
                        *appIsAllowed="{
                          permission:
                            'flow.coordinator.maintenance_notif.write',
                        }"
                        (click)="toggleStatusEdit()"
                        data-cy="maintenance-notification-status-edit-button"
                        class="status-edit-button"
                      >
                        <mat-icon class="edit-icon">edit</mat-icon>
                      </button>
                    }
                  </div>
                } @else {
                  @if (isManualStatusChangeEnabled()) {
                    <div class="flex--r-c">
                      <span>-</span>
                      <button
                        mat-icon-button
                        *appIsAllowed="{
                          permission:
                            'flow.coordinator.maintenance_notif.write',
                        }"
                        (click)="toggleStatusEdit()"
                        data-cy="maintenance-notification-status-edit-button"
                        class="status-edit-button"
                      >
                        <mat-icon class="edit-icon">edit</mat-icon>
                      </button>
                    </div>
                  } @else {
                    -
                  }
                }
              </span>
            </div>
          } @else {
            <div class="flex--r-c flex-1">
              <span>
                <div class="w-h-label html-overflow">
                  {{
                    'app.ui.maintenanceNotification.status.label' | translate
                  }}
                </div>
                @if (isManualStatusChangeEnabled() && isStatusEditing()) {
                  <mat-form-field
                    class="w-100 status-select"
                    data-cy="maintenance-notification-status-select"
                  >
                    <mat-select
                      [formControl]="maintenanceNotificationStatusControl"
                    >
                      @for (
                        status of $ENUMS().MAINTENANCE_NOTIFICATION_STATUS;
                        track status.id
                      ) {
                        <mat-option [value]="status.id">{{
                          status.translateKey | translate
                        }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                } @else if (entryDOM?.maintenanceNotificationStatus) {
                  <div class="flex--r-c gap--8 status-wrapper">
                    <div
                      class="wh-status"
                      [ngClass]="
                        entryDOM?.maintenanceNotificationStatus
                          | statusColor: 'maintenanceNotification'
                      "
                    >
                      {{
                        $ENUMS_OBJECT().MAINTENANCE_NOTIFICATION_STATUS[
                          entryDOM?.maintenanceNotificationStatus
                        ] | translate
                      }}
                    </div>
                    @if (isManualStatusChangeEnabled()) {
                      <button
                        mat-icon-button
                        *appIsAllowed="{
                          permission:
                            'flow.coordinator.maintenance_notif.write',
                        }"
                        (click)="toggleStatusEdit()"
                        data-cy="maintenance-notification-status-edit-button"
                        class="status-edit-button"
                      >
                        <mat-icon class="edit-icon">edit</mat-icon>
                      </button>
                    }
                  </div>
                } @else {
                  @if (isManualStatusChangeEnabled()) {
                    <div class="flex--r-c gap--8">
                      <span>-</span>
                      <button
                        mat-icon-button
                        *appIsAllowed="{
                          permission:
                            'flow.coordinator.maintenance_notif.write',
                        }"
                        (click)="toggleStatusEdit()"
                        data-cy="maintenance-notification-status-edit-button"
                        class="status-edit-button"
                      >
                        <mat-icon>edit</mat-icon>
                      </button>
                    </div>
                  } @else {
                    -
                  }
                }
              </span>
            </div>
          }
        </mat-grid-tile>

        <!-- Row 2: MN Type | Equipment | BomNode | (empty) -->
        <mat-grid-tile>
          <div class="flex-1">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.defect.model.maintenanceNotificationType' | translate }}
            </div>
            <div class="default-value">
              {{
                entryDOM?.type
                  ? ($ENUMS_OBJECT().MAINTENANCE_NOTIFICATION_TYPE[
                      entryDOM?.type
                    ] | translate)
                  : '-'
              }}
            </div>
          </div>
        </mat-grid-tile>

        <mat-grid-tile>
          <div class="flex-1 min-w-0">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.model.equipment' | translate }}
            </div>
            <app-w-h-chip-list
              style="max-width: fit-content; display: block"
              [data]="
                rootBomNode
                  ? [{ name: rootBomNode.name, icon: equipmentIcon }]
                  : []
              "
              icon="true"
              [tooltipKeys]="['name']"
              [titleKey]="'name'"
            ></app-w-h-chip-list>
          </div>
        </mat-grid-tile>

        <mat-grid-tile>
          <div class="flex-1 min-w-0">
            <div class="w-h-label entry-detail-label">
              {{ 'node.model.root' | translate }}
            </div>
            <app-w-h-chip-list
              style="max-width: fit-content; display: block"
              [data]="bomNode ? [bomNode] : []"
              icon="true"
              [tooltipKeys]="['name']"
              [titleKey]="'name'"
            ></app-w-h-chip-list>
          </div>
        </mat-grid-tile>

        <mat-grid-tile></mat-grid-tile>

        <!-- Row 3: Category | Error Code | Completion Date | Contact Person -->
        <mat-grid-tile>
          <div class="flex-1">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.defect.model.defectcategory' | translate }}
            </div>
            <div class="default-value">
              {{
                entryDOM?.category
                  ? ($ENUMS_OBJECT().MAINT_NOTIF_CATEGORY[entryDOM?.category]
                    | translate)
                  : '-'
              }}
            </div>
          </div>
        </mat-grid-tile>

        <mat-grid-tile>
          <div class="flex-1">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.defect.model.errorCode' | translate }}
            </div>
            <div class="default-value">
              {{
                entryDOM?.errorCode
                  ? ($ENUMS_OBJECT().MAINT_NOTIF_ERROR_CODE[entryDOM?.errorCode]
                    | translate)
                  : '-'
              }}
            </div>
          </div>
        </mat-grid-tile>

        <mat-grid-tile>
          <div class="flex-1">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.defect.model.completionTime' | translate }}
            </div>
            <div class="default-value">
              {{
                entryDOM?.requiredCompletionDate
                  ? (entryDOM?.requiredCompletionDate
                    | date: 'short' : '' : currentLocale())
                  : '-'
              }}
            </div>
          </div>
        </mat-grid-tile>

        <mat-grid-tile>
          <ng-container
            *ngTemplateOutlet="
              editableTextField;
              context: {
                label: 'workstep.model.contactperson',
                field: 'contactPerson',
                control: editForm.controls.contactPerson,
                value: entryDOM?.contactPerson,
                rows: 0,
              }
            "
          ></ng-container>
        </mat-grid-tile>

        <!-- Row 4: Created Date | Created By | Modified Date | Modified By -->
        <mat-grid-tile>
          <div class="flex-1">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.model.createddate' | translate }}
            </div>
            <div>
              {{ entryDOM?.createdDate | date: 'short' : '' : currentLocale() }}
            </div>
          </div>
        </mat-grid-tile>

        <mat-grid-tile>
          <div class="flex-1 min-w-0">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.model.createdbyworker' | translate }}
            </div>
            <ng-bee-worker-static-data
              [showLoginInfo]="true"
              [withDetailPageNav]="false"
              [detailURL]="entryDOM?.creatorEmployeeDetailURL"
            >
            </ng-bee-worker-static-data>
          </div>
        </mat-grid-tile>

        <mat-grid-tile>
          <div class="flex-1">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.model.modifiedDate' | translate }}
            </div>
            <div>
              {{
                entryDOM?.modifiedDate
                  ? (entryDOM?.modifiedDate
                    | date: 'short' : '' : currentLocale())
                  : '-'
              }}
            </div>
          </div>
        </mat-grid-tile>

        <mat-grid-tile>
          <div class="flex-1 min-w-0">
            <div class="w-h-label entry-detail-label">
              {{ 'entry.model.modifiedBy' | translate }}
            </div>
            @if (entryDOM?.lastModifierEmployeeDetailURL) {
              <ng-bee-worker-static-data
                [showLoginInfo]="true"
                [withDetailPageNav]="false"
                [detailURL]="entryDOM?.lastModifierEmployeeDetailURL"
              >
              </ng-bee-worker-static-data>
            } @else {
              -
            }
          </div>
        </mat-grid-tile>

        <!-- Row 5: Title (full width) -->
        <mat-grid-tile colspan="4">
          <ng-container
            *ngTemplateOutlet="
              editableTextField;
              context: {
                label: 'app.ui.symptomTitle.label',
                field: 'title',
                control: editForm.controls.title,
                value: entryDOM?.title,
                rows: 0,
              }
            "
          ></ng-container>
        </mat-grid-tile>

        <!-- Row 6-7: Description (full width) -->
        <mat-grid-tile colspan="4" rowspan="2">
          <ng-container
            *ngTemplateOutlet="
              editableTextField;
              context: {
                label: 'app.ui.symptomDescription.label',
                field: 'description',
                control: editForm.controls.description,
                value: entryDOM?.description,
                rows: 3,
              }
            "
          ></ng-container>
        </mat-grid-tile>

        <!-- Row 8: Cost Center -->
        @if (costCenterList().length > 0 || entryDOM?.costCenter) {
          <mat-grid-tile data-cy="cost-center-detail">
            <div class="flex-1">
              <div class="w-h-label entry-detail-label">
                {{ 'app.ui.costCenter.label' | translate }}
              </div>
              <div class="default-value">
                {{
                  entryDOM?.costCenter
                    ? ($ENUMS_OBJECT().MAINT_NOTIF_COST_CENTER[
                        entryDOM?.costCenter
                      ] | translate)
                    : '-'
                }}
              </div>
            </div>
          </mat-grid-tile>

          <mat-grid-tile colspan="3"></mat-grid-tile>
        }

        <!-- Row 10: Files (full width) -->
        <mat-grid-tile colspan="4">
          <div class="flex-1">
            <span class="w-h-label truncate-text">
              {{ 'equipment.ui.tab.file.tooltip' | translate }}
            </span>
            <div class="wh-assignment-box">
              <w-h-file-dnd-zone
                [targetId]="entryDOM?.id"
                [type]="'MAINTENANCE_NOTIFICATION'"
                [uploadSuccessNotification]="'nodefile.ui.uploadsuccess.notification'"
                [deleteSuccessNotification]="'nodefile.ui.deletesuccess.notification'"
                [isReadOnly]="!canEditPictures()"
                [isStaged]="true"
              ></w-h-file-dnd-zone>
            </div>
          </div>
        </mat-grid-tile>
      </mat-grid-list>
    </div>
    <div
      class="detail-actions-wrapper flex--r-fe-fe gap--12 p-t--8 m-t--8"
      style="border-top: 1px solid #e8e8e8"
    >
      <div
        class="manager-comment-column"
        [class.is-editing]="isFieldEditing('managerComment')"
      >
        <ng-container
          *ngTemplateOutlet="
            editableTextField;
            context: {
              label: 'entry.model.managercomment',
              field: 'managerComment',
              control: editForm.controls.managerComment,
              value: entryDOM?.managerComment,
              rows: 2,
            }
          "
        ></ng-container>
      </div>

      <div class="flex-1 m-b--8 gap--8 flex--r-c-fe">
        @if (
          entryDOM?.type &&
          entryDOM?.maintenanceNotificationStatus !== 'REJECTED' &&
          entryDOM?.maintenanceNotificationStatus !== 'SENT_TO_SAP' &&
          !entryDOM?.generatedWorkObjectID
        ) {
          @if (
            (isConfirmCaseCreationEnabled &&
              entryDOM?.type?.toLowerCase().includes('fault')) ||
            (!isAutomaticProjectCreationEnabled &&
              !entryDOM?.type?.toLowerCase().includes('fault'))
          ) {
            <button
              *appIsAllowed="{
                operator: 'AND',
                permission: isExternalReplyEnabled()
                  ? ['flow.coordinator.maintenance_notif.write']
                  : entryDOM?.type?.toLowerCase().includes('fault')
                    ? [
                        'flow.coordinator.maintenance_notif.write',
                        'flow.coordinator.case.create',
                      ]
                    : [
                        'flow.coordinator.maintenance_notif.write',
                        'flow.coordinator.project.create',
                      ],
              }"
              (click)="generateWorkObject()"
              mat-flat-button
              color="accent"
              data-cy="maintenance-notification-accept-button"
              [disabled]="hasChanges || loading"
            >
              <mat-icon>done</mat-icon>
              {{
                isExternalReplyEnabled()
                  ? ('maintenancenotifications.button.sendtosap'
                    | translate
                    | uppercase)
                  : entryDOM?.type?.toLowerCase().includes('fault')
                    ? ('maintenancenotification.button.acceptandgeneratecase'
                      | translate
                      | uppercase)
                    : ('maintenancenotification.button.acceptandgenerateproject'
                      | translate
                      | uppercase)
              }}
            </button>
            <button
              *appIsAllowed="{
                operator: 'AND',
                permission: entryDOM?.type?.toLowerCase().includes('fault')
                  ? [
                      'flow.coordinator.maintenance_notif.write',
                      'flow.coordinator.case.create',
                    ]
                  : [
                      'flow.coordinator.maintenance_notif.write',
                      'flow.coordinator.project.create',
                    ],
              }"
              (click)="handleDecline()"
              mat-flat-button
              color="warn"
              data-cy="maintenance-notification-decline-button"
              [disabled]="hasNonCommentChanges() || loading"
            >
              <mat-icon>call_missed</mat-icon>
              {{ 'ui.decline.label' | translate | uppercase }}
            </button>
          }
        }
        <w-h-button
          dataCy="cancel-button"
          [text]="'app.cancel.label' | translate | uppercase"
          matIcon="close"
          [disabled]="loading"
          (clicked)="isEditing() ? handleCancel() : handleClose()"
        />
        <w-h-button
          dataCy="save-button"
          [text]="'app.save.label' | translate | uppercase"
          matIcon="save"
          type="flat"
          color="accent"
          [disabled]="!hasChanges"
          [isLoading]="loading"
          (clicked)="handleSave()"
        />
      </div>
    </div>
  `,
  styles: `
    .detail-actions-wrapper {
      padding-top: 8px !important;
    }

    /*
      Plain block column so the label and the field/value below it always start
      at the left edge and span the full width — a flex column would size them
      to their content instead. While read-only it stays narrow so the action
      buttons (e.g. ACCEPT AND GENERATE PROJECT) have room for their labels; it
      only takes half the row while the comment is being edited.
    */
    .manager-comment-column {
      display: block;
      flex: 0 1 30%;
      min-width: 0;
      align-self: stretch;
      text-align: left;

      &.is-editing {
        flex: 1 1 0;
      }
    }
    .detail-card-header {
      color: var(--primary-color-50);
      font-size: 18px;
    }

    .generated-workobject-button {
      mat-icon {
        display: none;
      }

      &:hover mat-icon {
        display: flex;
      }
    }

    .card-body {
      max-height: 82vh;
      height: 82vh;
      overflow-y: auto;
    }

    .wh-assignment-box {
      margin-right: 8px;
    }

    .edit-icon {
      color: var(--accent-color-500);
    }

    .status-select {
      margin-top: -0.2em !important;
    }

    .status-wrapper:has(.edit-icon) {
      margin-top: -0.9em;
    }

    /*
      Every editable field has to fill its container (the footer column does
      not stretch its children) and stay inside the grid tile / the dialog.
    */
    .editable-field {
      width: 100%;
      max-width: 100%;
      overflow: hidden;
    }

    .field-edit-wrapper,
    .field-value-wrapper {
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      padding-right: 16px;
    }

    .field-edit-input {
      box-sizing: border-box;
      width: 100%;
      max-width: 100%;
      margin-top: -0.2em !important;
    }

    .field-value-wrapper:has(.field-edit-button) {
      margin-top: -0.6em;
    }

    .field-value-clamp {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      min-width: 0;
      overflow: hidden;
      word-break: break-word;
    }

    .field-edit-button {
      flex: 0 0 auto;
    }

    :host::ng-deep {
      mat-grid-list {
        margin-bottom: 0px;
        margin-right: 0px;
      }

      .mdc-button__label {
        min-width: 0px;
      }

      mat-grid-tile > div {
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
      }
    }
  `,
})
export class EntryMaintenanceNotificationDetailsComponent {
  store = inject(MaintenanceNotificationsStore);
  currentLocale = inject(UIService).$currentLocale;
  $ENUMS_OBJECT = inject(WHEnumDataService).$ENUMS_OBJECT;
  $ENUMS = inject(WHEnumDataService).$ENUMS;
  loginDataService = inject(WHLoginDataService);
  formReferenceService = inject(FormReferenceService);

  private readonly hasWritePermission = toSignal(
    this.loginDataService.permissions$.pipe(
      map(
        (p) => p?.includes('flow.coordinator.maintenance_notif.write') ?? false,
      ),
    ),
    { initialValue: false },
  );

  router = inject(Router);
  isRevertToDraftEnabled = false;
  isEditFeatureEnabled = false;
  isExternalReplyEnabled = signal(false);
  isConfirmCaseCreationEnabled = false;
  isAutomaticProjectCreationEnabled = false;
  isProjectsOrCasesEnabled = signal(false);
  maintenance: WHMaintenanceNotificationDOM & {
    workObjectStatusId?: number;
    workObjectDetails?: any;
  } = null;
  readonly editForm = new FormGroup({
    title: new FormControl(''),
    description: new FormControl(''),
    managerComment: new FormControl(''),
    contactPerson: new FormControl(''),
  });
  maintenanceNotificationStatusControl = new FormControl(null);
  /** Optional cost center, driven by the tenant-configurable COST_CENTER enum */
  costCenterList = computed<MetadataEnum[]>(
    () => (this.$ENUMS() as any).MAINT_NOTIF_COST_CENTER ?? [],
  );
  isManualStatusChangeEnabled = signal(false);
  isStatusEditing = signal(false);
  readonly equipmentIcon = WHIconENUM.EquipmentIcon as string;
  /** Fields the user switched into edit mode via the pen icon. */
  readonly editingFields = signal<ReadonlySet<string>>(new Set<string>());
  private readonly fileDropZone = viewChild(WHFileDragNDropComponent);
  private readonly editFormValues = signal<Record<string, string>>({});
  private readonly isFormInitialized = signal(false);
  private readonly statusControlValue = signal<string | null>(null);
  private loadedMaintenanceNotificationId: string | null = null;

  private readonly isTerminalStatus = computed(() => {
    const status =
      this.store.maintenanceNotification()?.maintenanceNotificationStatus;
    return !!status && TERMINAL_MN_STATUSES.includes(status);
  });

  /**
   * Mirrors `MaintenanceNotificationEditGuard.assertTextEditable` on the backend.
   * The write permission itself is checked in the template via `*appIsAllowed`.
   */
  readonly canEditTextFields = computed(() => {
    if (this.isTerminalStatus()) {
      return false;
    }

    if (this.isExternalReplyEnabled()) {
      // External/SAP tenants may only edit before the MN has been sent.
      const status =
        this.store.maintenanceNotification()?.maintenanceNotificationStatus;
      return !status || status === 'CREATED';
    }

    return true;
  });

  /** Pictures are FLOW-only, so only a terminal status and missing write permission block them. */
  readonly canEditPictures = computed(
    () => !this.isTerminalStatus() && this.hasWritePermission(),
  );

  private readonly changedFields = computed(() => {
    const maintenance = this.store.maintenanceNotification();
    const values = this.editFormValues();
    if (!this.isFormInitialized()) {
      return [];
    }
    return EDITABLE_MN_FIELDS.filter(
      (field) => (values[field] ?? '') !== (maintenance?.[field] ?? ''),
    );
  });

  /**
   * True while the user has a field open in edit mode or files staged — CANCEL
   * is only active then, since it leaves edit mode rather than the dialog.
   */
  readonly isEditing = computed(
    () =>
      this.editingFields().size > 0 ||
      this.isStatusEditing() ||
      (this.fileDropZone()?.hasStagedChanges() ?? false),
  );

  private readonly hasStatusChange = computed(() => {
    if (!this.isManualStatusChangeEnabled()) {
      return false;
    }
    const status = this.statusControlValue();
    return (
      !!status &&
      status !==
        this.store.maintenanceNotification()?.maintenanceNotificationStatus
    );
  });

  /**
   * Drives the interaction rule from the refinement: while anything is changed,
   * only SAVE and CANCEL stay active.
   */
  readonly hasUnsavedChanges = computed(
    () =>
      this.changedFields().length > 0 ||
      this.hasStatusChange() ||
      (this.fileDropZone()?.hasStagedChanges() ?? false),
  );

  /** True when there are unsaved changes beyond just the manager comment. */
  readonly hasNonCommentChanges = computed(
    () =>
      this.changedFields().some((f) => f !== 'managerComment') ||
      this.hasStatusChange() ||
      (this.fileDropZone()?.hasStagedChanges() ?? false),
  );

  constructor(
    public matDialogRef: MatDialogRef<EntryMaintenanceNotificationDetailsComponent>,
  ) {
    this.loginDataService.featureConfigMap$
      .pipe(untilDestroyed(this))
      .subscribe((featureConfigMap: Map<string, boolean>) => {
        this.isEditFeatureEnabled = featureConfigMap.get(
          WHFeatureKeyENUM.EDIT_DEFECT_ENTRY,
        );
        this.isRevertToDraftEnabled = featureConfigMap.get(
          WHFeatureKeyENUM.ENTRY_REVERT_TO_DRAFT,
        );
        this.isExternalReplyEnabled.set(
          featureConfigMap.get(
            WHFeatureKeyENUM.MAINTENANCE_NOTIFICATIONS_EXTERNAL_REPLY,
          ) ?? false,
        );
        this.isConfirmCaseCreationEnabled = featureConfigMap.get(
          WHFeatureKeyENUM.MAINTENANCE_NOTIFICATIONS_CONFIRM_CASE_CREATION,
        );
        this.isAutomaticProjectCreationEnabled = featureConfigMap.get(
          WHFeatureKeyENUM.MAINTENANCE_NOTIFICATIONS_AUTOMATIC_PROJECT_CREATION,
        );
        this.isProjectsOrCasesEnabled.set(
          featureConfigMap.get(WHFeatureKeyENUM.CORE_WORK_FLOW_PROJECT) ||
            featureConfigMap.get(WHFeatureKeyENUM.CORE_WORK_FLOW_CASE),
        );
        this.isManualStatusChangeEnabled.set(!this.isProjectsOrCasesEnabled());
      });

    this.editForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.editFormValues.set(
        this.editForm.getRawValue() as Record<string, string>,
      );
    });

    this.maintenanceNotificationStatusControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((status) => this.statusControlValue.set(status));

    effect(() => {
      const maintenance = this.store.maintenanceNotification();
      if (
        maintenance?.id &&
        maintenance.id !== this.loadedMaintenanceNotificationId
      ) {
        this.loadedMaintenanceNotificationId = maintenance.id;
        this.store.loadMaintenanceNotificationDetails({
          maintenanceNotificationId: maintenance.id,
        });
      }
    });

    effect(() => {
      const maintenance = this.store.maintenanceNotification();
      if (!maintenance) {
        return;
      }
      // A background refresh must never overwrite in-progress edits.
      if (untracked(() => this.isEditing())) {
        return;
      }
      this.resetControlsFromStore();
    });
  }

  isFieldEditing(field: string): boolean {
    return this.editingFields().has(field);
  }

  startFieldEdit(field: string): void {
    this.editingFields.update((fields) => new Set(fields).add(field));
  }

  toggleStatusEdit() {
    this.isStatusEditing.update((isEditing) => !isEditing);
  }

  navigateToProjectDetails() {
    this.matDialogRef.close();

    const maintenance = this.store.maintenanceNotification();
    const parts = maintenance?.generatedWorkObjectDetailURL?.split('/');
    const apiIndex = parts?.findIndex((part) => part === 'api');
    const path = parts?.slice(apiIndex + 1).join('/');

    if (path) {
      this.router.navigateByUrl(`/${path}`);
    }
  }

  generateWorkObject() {
    const maintenance = this.store.maintenanceNotification();
    if (!maintenance) {
      return;
    }

    this.store.dispatchMaintenanceNotification({
      maintenanceNotificationId: maintenance.id,
      onSuccess: () => {
        this.leaveEditMode();
        this.store.loadMaintenanceNotificationDetails({
          maintenanceNotificationId: maintenance.id,
        });
        this.store.loadMaintenanceNotifications({
          params: this.store.filters(),
        });
        if (!this.isExternalReplyEnabled()) {
          this.matDialogRef.close();
        }
      },
    });
  }

  handleSave() {
    const maintenance = this.store.maintenanceNotification();
    if (!maintenance) {
      return;
    }

    const payload: IWHUpdateMaintenanceNotificationPayload = {};

    if (this.hasStatusChange()) {
      payload.maintenanceNotificationStatus = this.statusControlValue();
    }

    const values = this.editFormValues();
    for (const field of this.changedFields()) {
      payload[MN_PAYLOAD_KEY_BY_FIELD[field]] = values[field] ?? '';
    }
    // The 10.4 backend reads only the legacy `comment` (WHP-13137).
    if (payload.description !== undefined) {
      payload.comment = payload.description;
    }

    const dropZone = this.fileDropZone();
    const filesToUpload = (dropZone?.stagedUploads() ?? []).map(
      ({ file }) => file,
    );
    const fileIdsToDelete = dropZone?.stagedDeletionIds() ?? [];

    if (
      Object.keys(payload).length === 0 &&
      filesToUpload.length === 0 &&
      fileIdsToDelete.length === 0
    ) {
      return;
    }

    this.store.saveMaintenanceNotificationEdit({
      maintenanceNotificationId: maintenance.id,
      payload,
      filesToUpload,
      fileIdsToDelete,
      onSuccess: () => {
        // Stay on the detail view — it now shows the persisted values, and
        // ACCEPT / DECLINE become available again.
        this.leaveEditMode();
        this.store.loadMaintenanceNotifications({
          params: this.store.filters(),
        });
      },
    });
  }

  /** Leaves edit mode and restores the persisted values; the dialog stays open. */
  handleCancel() {
    this.confirmDiscard(() => this.leaveEditMode());
  }

  /** Closes the whole dialog, asking first when there are unsaved changes. */
  handleClose() {
    this.confirmDiscard(() => {
      this.leaveEditMode();
      this.matDialogRef.close();
    });
  }

  private confirmDiscard(onDiscard: () => void): void {
    if (!this.hasUnsavedChanges()) {
      onDiscard();
      return;
    }

    this.formReferenceService
      .createDialog()
      .pipe(
        untilDestroyed(this),
        tap((canDiscard) => {
          if (canDiscard) {
            onDiscard();
          }
        }),
      )
      .subscribe();
  }

  handleDecline() {
    const maintenance = this.store.maintenanceNotification();
    if (!maintenance) {
      return;
    }

    const payload: IWHUpdateMaintenanceNotificationPayload = {
      maintenanceNotificationStatus: 'REJECTED',
    };
    // Only send the comment when it actually changed — an unchanged value would
    // make the backend treat this as a text edit and run the edit guard.
    if (this.changedFields().includes('managerComment')) {
      payload.managerComment = this.editFormValues()['managerComment'] ?? '';
    }

    this.store.updateMaintenanceNotification({
      maintenanceNotificationId: maintenance.id,
      payload,
      onSuccess: () => {
        this.store.loadMaintenanceNotifications({
          params: this.store.filters(),
        });
        this.matDialogRef.close();
      },
    });
  }

  /** Drops all pending edits and returns every field to its read-only state. */
  private leaveEditMode(): void {
    this.editingFields.set(new Set<string>());
    this.isStatusEditing.set(false);
    this.fileDropZone()?.resetStagedChanges();
    this.resetControlsFromStore();
  }

  private resetControlsFromStore(): void {
    const maintenance = this.store.maintenanceNotification();

    this.editForm.patchValue(
      {
        title: maintenance?.title || '',
        description: maintenance?.description || '',
        managerComment: maintenance?.managerComment || '',
        contactPerson: maintenance?.contactPerson || '',
      },
      { emitEvent: false },
    );
    this.editFormValues.set(
      this.editForm.getRawValue() as Record<string, string>,
    );
    this.isFormInitialized.set(true);

    if (this.isManualStatusChangeEnabled()) {
      const status = maintenance?.maintenanceNotificationStatus || null;
      this.maintenanceNotificationStatusControl.patchValue(status, {
        emitEvent: false,
      });
      this.statusControlValue.set(status);
    }
  }
}
